'use client';

import React, { useState } from 'react';
import { Gift, Info } from 'lucide-react';

const rewardOptions = [
  { id: 'reward-off', label: 'Off', value: 0 },
  { id: 'reward-5', label: '£5', value: 5 },
  { id: 'reward-10', label: '£10', value: 10 },
  { id: 'reward-20', label: '£20', value: 20 },
  { id: 'reward-50', label: '£50', value: 50 },
  { id: 'reward-custom', label: 'Custom', value: -1 },
];

const rewardStates = [
  { key: 'rstate-none', label: 'No Reward', color: 'bg-secondary text-muted-foreground' },
  { key: 'rstate-offered', label: 'Reward Offered', color: 'bg-amber-100 text-amber-700' },
  { key: 'rstate-funded', label: 'Reward Funded', color: 'bg-accent/10 text-accent' },
  { key: 'rstate-approved', label: 'Reward Approved', color: 'bg-green-100 text-green-700' },
  { key: 'rstate-paid', label: 'Reward Paid', color: 'bg-green-100 text-green-700' },
];

export default function RewardSelector() {
  const [selected, setSelected] = useState('reward-20');
  const [customValue, setCustomValue] = useState('');
  const [rewardState] = useState('rstate-offered');

  const activeReward = rewardOptions?.find((r) => r?.id === selected);
  const isCustom = selected === 'reward-custom';
  const hasReward = selected !== 'reward-off';

  const currentState = rewardStates?.find((s) => s?.key === rewardState);

  return (
    <div className="bg-white rounded-2xl border border-border shadow-card overflow-hidden">
      <div className="px-6 py-4 border-b border-border flex items-center gap-3">
        <div className="w-8 h-8 bg-accent/10 rounded-xl flex items-center justify-center">
          <Gift size={15} className="text-accent" />
        </div>
        <div className="flex-1">
          <h2 className="font-700 text-base text-foreground">Add Finder Reward</h2>
          <p className="text-xs text-muted-foreground">A small thank you can make a big difference.</p>
        </div>
        {hasReward && currentState && (
          <span className={`text-[10px] font-700 uppercase tracking-wide px-2.5 py-1 rounded-full ${currentState?.color}`}>
            {currentState?.label}
          </span>
        )}
      </div>

      <div className="p-6">
        {/* Reward amount selector */}
        <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 mb-5">
          {rewardOptions?.map(({ id, label }) => (
            <button
              key={id}
              onClick={() => setSelected(id)}
              className={`py-2.5 rounded-xl text-sm font-700 border transition-all duration-150 ${
                selected === id
                  ? 'bg-accent text-white border-accent shadow-accent-glow'
                  : 'bg-secondary text-foreground border-border hover:border-accent/40'
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        {/* Custom input */}
        {isCustom && (
          <div className="mb-5">
            <label className="block text-sm font-600 text-foreground mb-1.5">Custom reward amount</label>
            <div className="relative">
              <span className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground font-600">£</span>
              <input
                type="number"
                value={customValue}
                onChange={(e) => setCustomValue(e?.target?.value)}
                placeholder="0.00"
                min="1"
                max="500"
                className="w-full bg-secondary rounded-xl pl-8 pr-4 py-3 text-sm text-foreground outline-none border border-border focus:border-accent focus:bg-white transition-colors tabular-nums"
              />
            </div>
          </div>
        )}

        {/* Reward info */}
        {hasReward && (
          <div className="bg-accent/5 border border-accent/20 rounded-xl p-4 flex items-start gap-3">
            <Info size={15} className="text-accent mt-0.5 flex-shrink-0" />
            <div className="text-xs text-foreground/80 leading-relaxed">
              <p className="font-600 mb-1">How rewards work</p>
              <p>The reward is associated with this recovery session. A QR scan alone never earns a reward. The owner must confirm the return before the reward is approved. RETURNI uses a simulated payment flow in this prototype.</p>
            </div>
          </div>
        )}

        {!hasReward && (
          <p className="text-xs text-muted-foreground">
            No reward will be offered. You can change this at any time while the item is in Lost Mode.
          </p>
        )}

        {/* Reward state flow — demo */}
        <div className="mt-5">
          <p className="text-xs font-600 uppercase tracking-widest text-muted-foreground mb-3">Reward States (Demo)</p>
          <div className="flex flex-wrap gap-2">
            {rewardStates?.map(({ key, label, color }) => (
              <span key={key} className={`text-[10px] font-700 uppercase tracking-wide px-2.5 py-1 rounded-full ${color}`}>
                {label}
              </span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}